import { useParams, useNavigate } from "react-router-dom";
import { Form, Input, message } from "antd";
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { apiClient } from "../../../shared/api/AllApi";
import { fetchSystemConfig, readCachedSystemConfig } from "../../../shared/lib/systemConfig";
import { formatCurrency, toApiNumber } from "../../../utils/formatters";
import { FormPage, FormCard, inputStyle, T, cardStyle } from "../../../shared/ui/PageShell";

const IncreaseVendorOwed = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [systemConfig, setSystemConfig] = useState(() => readCachedSystemConfig());
  const [vendorInfo, setVendorInfo] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const mainCode = systemConfig?.currency_code || "IQD";

  useEffect(() => {
    fetchSystemConfig().then(setSystemConfig).catch(() => {});
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await apiClient.get(`/vendors/${id}`);
        if (cancelled) return;
        setVendorInfo({
          name: res?.data?.name || "",
          consignment_owed: toApiNumber(res?.data?.consignment_owed, 0),
        });
      } catch {
        if (!cancelled) setVendorInfo(null);
      }
    })();
    return () => { cancelled = true; };
  }, [id]);

  const amountNum = Math.abs(toApiNumber(amount, 0));

  const onSubmit = async () => {
    if (amountNum <= 0) {
      message.error(t("increaseFarmerOwedEnterAmount", "Enter an amount greater than zero."));
      return;
    }
    setSubmitting(true);
    try {
      await apiClient.post(`/vendors/${id}/increase-owed`, {
        amount: amountNum,
        note: note.trim() || undefined,
      });
      message.success(t("increaseFarmerOwedSuccess", "Amount owed to farmer recorded."));
      navigate("/vendors");
    } catch (error) {
      message.error(
        error?.response?.data?.message || t("increaseFarmerOwedError", "Recording failed.")
      );
    } finally {
      setSubmitting(false);
    }
  };

  const sidePanel = (
    <div style={cardStyle}>
      <div style={{ padding: "16px 18px" }}>
        <div style={{ fontSize: 11, color: T.muted, textTransform: "uppercase", letterSpacing: "0.05em" }}>
          {t("farmer", "Farmer")}
        </div>
        <div style={{ fontSize: 16, fontWeight: 800, color: T.text, marginTop: 3 }}>
          {vendorInfo?.name || "—"}
        </div>

        <div style={{ marginTop: 14, paddingTop: 14, borderTop: `1px solid ${T.borderL}` }}>
          <div style={{ fontSize: 11, color: T.muted, textTransform: "uppercase", letterSpacing: "0.05em" }}>
            {t("vendorRemainingToClear", "Owed to farmer")}
          </div>
          <div style={{ fontSize: 22, fontWeight: 900, color: T.red, marginTop: 3 }}>
            {formatCurrency(vendorInfo?.consignment_owed || 0, mainCode)}
          </div>
        </div>

        <div style={{ marginTop: 14, paddingTop: 14, borderTop: `1px solid ${T.borderL}` }}>
          <div style={{ fontSize: 11, color: T.muted, textTransform: "uppercase", letterSpacing: "0.05em" }}>
            {t("increaseFarmerOwedImpact", "Amount added to what we owe")}
          </div>
          <div style={{ fontSize: 20, fontWeight: 900, color: "#d97706", marginTop: 3 }}>
            + {formatCurrency(amountNum, mainCode)}
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <FormPage
      breadcrumb={[
        t("sidebarSectionContacts", "Contacts"),
        t("vendorsMenu", "Farmers"),
        t("increaseFarmerOwedTitle", "Increase Amount Owed to Farmer"),
      ]}
      title={t("increaseFarmerOwedTitle", "Increase Amount Owed to Farmer")}
      subtitle={
        vendorInfo?.name
        || t(
          "increaseFarmerOwedSubtitle",
          "Adds to the farmer’s remaining balance (what the shop still owes them)."
        )
      }
      onBack={() => navigate("/vendors")}
      onSave={onSubmit}
      saving={submitting}
      sidePanel={sidePanel}
    >
      <Form layout="vertical">
        <FormCard
          title={t("increaseFarmerOwedTitle", "Increase Amount Owed to Farmer")}
          subtitle={t(
            "increaseFarmerOwedSubtitle",
            "Adds to the farmer’s remaining balance (what the shop still owes them)."
          )}
        >
          <Form.Item label={t("increaseFarmerOwedAmount", "Amount ({{code}})", { code: mainCode })}>
            <Input
              type="number"
              min={0}
              step="any"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="0"
              style={inputStyle}
            />
          </Form.Item>
          <Form.Item label={t("note", "Note")}>
            <Input.TextArea
              rows={2}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder={t("optional", "Optional")}
              style={inputStyle}
            />
          </Form.Item>
        </FormCard>
      </Form>
    </FormPage>
  );
};

export default IncreaseVendorOwed;
